import React, { useEffect, useRef, useState } from 'react';
import { useGoogleAds } from '../../hooks/useGoogleAds';
import { AdConfig } from '../../lib/googleAds';
import { adAnalyticsService } from '../../lib/adAnalytics';
import { googleAdsService } from '../../lib/googleAds';

interface AdComponentProps {
  /** Ad slot ID (optional) */
  adSlot?: string;
  /** Ad format */
  format?: 'auto' | 'rectangle' | 'vertical' | 'horizontal';
  /** Whether the ad should be responsive */
  responsive?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Fallback content when ads are disabled or fail to load */
  fallback?: React.ReactNode;
  /** Whether to show loading state */
  showLoading?: boolean;
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
  /** Error handling callback */
  onError?: (error: string) => void;
  /** Success callback */
  onLoad?: () => void;
  /** Test mode - shows placeholder instead of real ad */
  testMode?: boolean;
  /** Ad type for analytics */
  adType?: 'treasure-card' | 'sidebar';
  /** Ad position for analytics */
  position?: number;
}

/**
 * Base Google Ad component with error handling and fallback states
 */
export const AdComponent: React.FC<AdComponentProps> = ({
  adSlot,
  format = 'auto',
  responsive = true,
  className = '',
  fallback = null,
  showLoading = true,
  loadingComponent,
  onError,
  onLoad,
  testMode = false,
  adType = 'treasure-card',
  position
}) => {
  const { isEnabled, isAvailable, displayAd, error } = useGoogleAds();
  const adContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (testMode) {
      setHasLoaded(true);
      onLoad?.();
      return;
    }

    if (!isAvailable) {
      setLocalError('Google Ads service not available');
      onError?.('Google Ads service not available');
      return;
    }

    const loadAd = async () => {
      if (!adContainerRef.current || isInitialized) return;

      setIsLoading(true);
      setLocalError(null);
      setIsInitialized(true);

      const startTime = performance.now();

      try {
        const config: AdConfig = {
          pubId: '', // Will be set by the service
          adSlot,
          adType,
          format,
          responsive,
          className: `ad-component ${className}`
        };

        const adElement = await displayAd(config);
        
        if (adElement && adContainerRef.current) {
          // Clear container and append ad
          adContainerRef.current.innerHTML = '';
          adContainerRef.current.appendChild(adElement);
          
          // Now push to AdSense after element is in DOM
          try {
            // @ts-ignore - adsbygoogle is loaded dynamically
            (window.adsbygoogle = window.adsbygoogle || []).push({});
            
            const loadTime = performance.now() - startTime;
            
            // Track successful load and impression
            adAnalyticsService.trackLoad(adType, loadTime, position);
            adAnalyticsService.trackImpression(adType, position);
            googleAdsService.trackImpression();
            
            setHasLoaded(true);
            onLoad?.();
            
            console.log(`Google Ads: Ad pushed successfully for ${adType} at position ${position}`);
          } catch (pushError) {
            console.error('Google Ads: Failed to push ad:', pushError);
            throw new Error('Failed to initialize ad');
          }
        } else {
          throw new Error('Failed to create ad element');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load ad';
        
        // Track error
        adAnalyticsService.trackError(adType, errorMessage, position);
        
        setLocalError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadAd();

    // Cleanup function to reset initialization state
    return () => {
      setIsInitialized(false);
    };
  }, [isEnabled, isAvailable, displayAd, adSlot, format, responsive, className, onError, onLoad, testMode]);

  // Handle service-level errors
  useEffect(() => {
    if (error) {
      setLocalError(error);
      onError?.(error);
    }
  }, [error, onError]);

  // Don't render anything if ads are disabled
  if (!isEnabled) {
    return <>{fallback}</>;
  }

  // Show test placeholder in test mode
  if (testMode) {
    return (
      <div className={`ad-component ad-test-mode ${className}`}>
        <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
          <div className="text-gray-500 text-sm font-medium mb-2">
            📢 Advertisement Placeholder
          </div>
          <div className="text-xs text-gray-400">
            {format} • {responsive ? 'Responsive' : 'Fixed'}
            {adSlot && ` • Slot: ${adSlot}`}
            {adType && ` • Type: ${adType}`}
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading && showLoading) {
    return (
      <div className={`ad-component ad-loading ${className}`}>
        {loadingComponent || (
          <div className="bg-gray-50 rounded-lg p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        )}
      </div>
    );
  }

  // Show error state or fallback
  if (localError && !hasLoaded) {
    console.warn('AdComponent error:', localError);
    return <>{fallback}</>;
  }

  // Handle click tracking
  const handleClick = () => {
    adAnalyticsService.trackClick(adType, position);
  };

  // Main ad container
  return (
    <div 
      ref={adContainerRef}
      className={`ad-component ${className}`}
      data-ad-format={format}
      data-responsive={responsive}
      onClick={handleClick}
    />
  );
};

export default AdComponent;